import { apiRequest } from './client.js';

/**
 * Menu Master (M02 §10 Menu Management Engine) — real /v1/menus contract.
 * Every menu item is dynamic; §10 fields: Menu Code, Menu Name, Parent Menu,
 * Menu Sequence, Web Visible, Mobile Visible, Icon, URL, Status, Role Mapping.
 */
export interface MenuItem {
  readonly menuId: string;
  readonly menuCode: string;
  readonly menuName: string;
  readonly parentMenuCode: string | null;
  readonly menuSequence: number;
  readonly webVisible: boolean;
  readonly mobileVisible: boolean;
  readonly icon: string | null;
  readonly url: string | null;
  readonly recordStatusCode: string;
  readonly effectiveFromDate?: string;
  readonly effectiveToDate?: string;
  readonly version: number;
}

/** T019 role-menu mapping (which roles see a menu + their per-menu actions). */
export interface RoleMenuMap {
  readonly mapId: string;
  readonly roleId: string;
  readonly menuId: string;
  readonly canView: boolean;
  readonly canCreate: boolean;
  readonly canEdit: boolean;
  readonly canDelete: boolean;
  readonly canApprove: boolean;
  readonly canReject: boolean;
  readonly canExport: boolean;
  readonly canPrint: boolean;
  readonly recordStatusCode: string;
}

export interface MenuPermissionFlags {
  canView?: boolean;
  canCreate?: boolean;
  canEdit?: boolean;
  canDelete?: boolean;
  canApprove?: boolean;
  canReject?: boolean;
  canExport?: boolean;
  canPrint?: boolean;
}

export interface CreateMenuInput {
  readonly menuCode: string;
  readonly menuName: string;
  readonly parentMenuCode?: string;
  readonly menuSequence?: number;
  readonly webVisible?: boolean;
  readonly mobileVisible?: boolean;
  readonly icon?: string;
  readonly url?: string;
  readonly effectiveFromDate: string;
}

export interface UpdateMenuInput {
  readonly menuName?: string;
  readonly parentMenuCode?: string;
  readonly menuSequence?: number;
  readonly webVisible?: boolean;
  readonly mobileVisible?: boolean;
  readonly icon?: string;
  readonly url?: string;
  readonly effectiveToDate?: string;
  readonly version: number;
}

/** §10 Status values a menu item can hold. */
export const MENU_STATUSES = ['ACTIVE', 'INACTIVE'] as const;
export type MenuStatus = (typeof MENU_STATUSES)[number];

export const MENU_STATUS_LABELS: Readonly<Record<string, string>> = {
  ACTIVE: 'Active',
  INACTIVE: 'Inactive',
};

type Tok = string | null | undefined;

function unwrap(r: { items: readonly MenuItem[] } | readonly MenuItem[]): readonly MenuItem[] {
  return Array.isArray(r) ? r : (r as { items: readonly MenuItem[] }).items;
}

/** GET /v1/menus — menu items for the tenant (ACTIVE only unless includeInactive). */
export async function listMenus(includeInactive = false, token?: Tok): Promise<readonly MenuItem[]> {
  const qs = includeInactive ? '?includeInactive=true' : '';
  const r = await apiRequest<{ items: readonly MenuItem[] } | readonly MenuItem[]>(
    `/menus${qs}`,
    { token },
  );
  return unwrap(r);
}

/** POST /v1/menus — create a menu item. */
export function createMenu(body: CreateMenuInput, token?: Tok): Promise<MenuItem> {
  return apiRequest<MenuItem>('/menus', { method: 'POST', token, body });
}

/** PATCH /v1/menus/:menuId — update §10 fields (optimistic-locked via version). */
export function updateMenu(menuId: string, body: UpdateMenuInput, token?: Tok): Promise<MenuItem> {
  return apiRequest<MenuItem>(`/menus/${menuId}`, { method: 'PATCH', token, body });
}

/** POST /v1/menus/:menuId/deactivate — set status INACTIVE. */
export function deactivateMenu(menuId: string, token?: Tok): Promise<MenuItem> {
  return apiRequest<MenuItem>(`/menus/${menuId}/deactivate`, { method: 'POST', token });
}

/** POST /v1/menus/:menuId/activate — set status ACTIVE. */
export function activateMenu(menuId: string, token?: Tok): Promise<MenuItem> {
  return apiRequest<MenuItem>(`/menus/${menuId}/activate`, { method: 'POST', token });
}

/** GET /v1/menus/:menuId/roles — active role mappings for a menu (§10 Role Mapping). */
export function listMenuRoles(menuId: string, token?: Tok): Promise<readonly RoleMenuMap[]> {
  return apiRequest<readonly RoleMenuMap[]>(`/menus/${menuId}/roles`, { token });
}

/** GET /v1/menus/role/:roleId — every active menu mapping one role holds. */
export function listRoleMenus(roleId: string, token?: Tok): Promise<readonly RoleMenuMap[]> {
  return apiRequest<readonly RoleMenuMap[]>(`/menus/role/${roleId}`, { token });
}

/** POST /v1/menus/role-menu — assign (or update) a role→menu mapping. */
export function assignMenuRole(
  menuId: string,
  roleId: string,
  perms: MenuPermissionFlags,
  token?: Tok,
): Promise<RoleMenuMap> {
  return apiRequest<RoleMenuMap>('/menus/role-menu', {
    method: 'POST',
    token,
    body: { menuId, roleId, canView: true, ...perms },
  });
}

/** DELETE /v1/menus/role-menu?roleId=&menuId= — remove a role→menu mapping. */
export function revokeMenuRole(menuId: string, roleId: string, token?: Tok): Promise<void> {
  const qs = `?roleId=${encodeURIComponent(roleId)}&menuId=${encodeURIComponent(menuId)}`;
  return apiRequest<void>(`/menus/role-menu${qs}`, { method: 'DELETE', token });
}
